import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { NewRelicCustomAttribute } from '@cof/refi-web-sdk/dist/utils/stratum';
import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { DebugService } from '../debug/debug.service.js';
import { MatchingState, CustomerOffer, OfferState } from '../../models/search-customer-offers.model.js';
import { ApiService } from '../api/api.service.js';
import { REFI_WEB_UI_CONST } from '../../constants/constants.js';
import { AppService } from '../app/app.service.js';
import SessionService from '../session/session.service.js';
import { stratum } from '../../constants/stratum.js';

export class _CustomerOffersService {
  constructor() {
    this.debug = new DebugService('CustomerOffersService');
    this.offer = null;
    this.filledPromotionalCode = false;
    this.matchingState = MatchingState.none;
  }

  async getCustomerOffers(searchCustomerOffersRequest, matchingState, useTokenizedTaxId = false) {
    // Early return if offer already exists
    if (this.offer) {
      this.debug.log('getCustomerOffers returning existing result');
      return this.offer;
    }

    if (this.usedOfferPage()) {
      trackAnalyticsEvent('offer search used offer page', '', 'msg', 'message');
    }

    this.matchingState = matchingState;
    const requestBodyExists = Object.keys(searchCustomerOffersRequest).length > 0;

    // Publish analytics and start API call
    stratum.publishTag(TagKey.SEARCH_CUSTOMER_OFFERS_OFFERS_CALL_ATTEMPTED, {
      replacements: { value: matchingState },
    });
    trackAnalyticsEvent(`offer search ${matchingState}`, '', 'msg', 'message');

    try {
      const url = this._buildApiUrl(useTokenizedTaxId);
      const apiResponse = await ApiService.post(url, ...(requestBodyExists ? [searchCustomerOffersRequest] : []));

      const offerResponse = apiResponse?.customerOffer || {};
      const processedOffer = this._processOfferResponse(offerResponse, matchingState, requestBodyExists, searchCustomerOffersRequest);

      this.offer = processedOffer;
      return this.offer;
    } catch (error) {
      trackAnalyticsEvent(`customer offer search ${matchingState} error`, '', 'error', 'message');
      stratum.publishTag(TagKey.SEARCH_CUSTOMER_OFFERS_OFFERS_CALL_FAILED);
      this.offer = null;
      return null;
    }
  }

  _buildApiUrl(useTokenizedTaxId) {
    const url = new URL(REFI_WEB_UI_CONST.API.searchCustomerOffers, window.location.origin);
    if (useTokenizedTaxId) {
      url.searchParams.set('useTokenizedTaxId', 'true');
    }
    return url.toString();
  }

  _processOfferResponse(offerResponse, matchingState, requestBodyExists, searchCustomerOffersRequest) {
    if (CustomerOffer.isValid(offerResponse)) {
      this._handleValidOffer(offerResponse, matchingState);
    } else {
      this._handleInvalidOffer(offerResponse, matchingState);
    }

    // Set common properties
    offerResponse.cachedTaxId = !requestBodyExists;
    offerResponse.lastFourTaxId = searchCustomerOffersRequest?.customerTaxpayerIdLastFourDigits;

    return offerResponse;
  }

  _handleValidOffer(offerResponse, matchingState) {
    // Set promo codes
    AppService.setPromoCode(offerResponse.promotionalCode);
    SessionService.setPromoCode(offerResponse.promotionalCode);
    this.filledPromotionalCode = true;

    // Analytics and session management
    trackAnalyticsEvent('offer search applied code', '', 'msg', 'message');
    if (offerResponse?.offerType) {
      SessionService.setProductTypeInSession(offerResponse.offerType);
      stratum.setPluginVar(REFI_WEB_UI_CONST.NEW_RELIC_PLUGIN_NAME, NewRelicCustomAttribute.PRODUCT_TYPE, offerResponse.offerType);
    }

    stratum.publishTag(TagKey.SEARCH_CUSTOMER_OFFERS_OFFER_APPLIED, {
      replacements: { value: offerResponse.promotionalCode },
    });

    // Set offer state
    const externalID = AppService.getExternalId();
    offerResponse.offerState = externalID?.valid && externalID?.expired ? OfferState.new : OfferState.matched;

    trackAnalyticsEvent(`customer offer search ${matchingState} ${offerResponse.offerState}`, '', 'msg', 'message');
  }

  _handleInvalidOffer(offerResponse, matchingState) {
    // Reset state for invalid offers
    this.matchingState = MatchingState.none;
    AppService.setPromoCode('');
    SessionService.setPromoCode('');
    this.filledPromotionalCode = false;
    offerResponse.offerState = OfferState.expired;

    // Analytics
    trackAnalyticsEvent(`customer offer search ${matchingState} invalid`, '', 'msg', 'message');
    trackAnalyticsEvent(`customer offer search ${matchingState} ${offerResponse.offerState}`, '', 'msg', 'message');
    stratum.publishTag(TagKey.SEARCH_CUSTOMER_OFFERS_NO_VALID_OFFERS);
  }

  filledPromotionalCodeFromSearch() {
    return this.filledPromotionalCode;
  }

  usedOfferPage() {
    return sessionStorage.getItem('auto-refi-used-offer-page') === 'true';
  }

  getMatchingState() {
    return this.matchingState;
  }

  getOffer() {
    return this.offer;
  }

  removeOffer() {
    SessionService.removeCachedOffer();
    this.offer = null;
  }

  recoverOfferFromCache() {
    this.offer = SessionService.getCachedOffer();
    if (this.offer) {
      AppService.setPromoCode(this.offer.promotionalCode);
      SessionService.setPromoCode(this.offer.promotionalCode);
    }
  }
}
export const CustomerOffersService = new _CustomerOffersService();
